import { useContext } from "react";
import { pathConstants } from "./pathContants";
import appPages, {
  AllGoodsReceived,
  AllPurchaseOrders,
  CreateGoodsReceived,
  CreatePurchaseOrder,
  GoodsReceivedDetails,
  PurchaseOrderDetails,
  AllGoals,
  GoalsDetails,
  EditPurchaseOrder,
  EditGoodsReceived,
  AllBills,
  CreateBills,
  BillsDetails,
  EditBills,
  AllPOCategories,
  AllIntegratedTerminals,
  EditIntegratedTerminals,
  CreateBulkExpense,
  BusinessSupplierConsentHome,
  AllActivityLogs,
  ActivityLogDetails,
  AllBusinessConnections,
} from "../pages/appPages";
import { getPlatform } from "../helpers";
import { platformHandler } from "../hooks/context";
import authPages from "../pages/authPages";
// import storePages from "../pages/storePages";
import secondaryComponents from "../components/secondaryComponents";
import publicPages from "../pages/publicPages";
import { Navigate, Outlet } from "react-router-dom";
import RoutePermissionRedirector from "../components/secondaryComponents/RoutePermissionRedirector";
import Error404 from "../pages/ErrorPages/Error404";
import SectionPages from "../pages/sectionPages";
import PushNotifications from "../components/secondaryComponents/PushNotifications";
import {
  ClientLogin,
  ExistingClientConnect,
  MobileComTest,
} from "../pages/HelperPages";
import {
  AccountingLayout,
  AllSectionsLayout,
  AppLayout,
  CustomersLayout,
  CustomerSubLayout,
  DashboardLayout,
  ExpensesLayout,
  InventoryLayout,
  MarketingLayout,
  MenuPageLayout,
  MobileAppLayout,
  ProcurementLayout,
  PerformanceLayout,
  SectionLayout,
  // SellModeLayout,
  SellModeLayoutV2,
  SettingsLayout,
  StoresLayout,
  SubAccountLayout,
  TagsLayout,
  TerminalsLayout,
  TransactionsLayout,
  TransfersLayout,
  SmsCampaignLayout,
} from "../layouts";
import {
  PointOfSaleProducts,
  CheckoutHome,
  OrderExtra,
  BillCheckoutContinuation,
  SMReconciliationHome,
  SMExpenseHome,
  BillsHome,
  PendingCheckoutHome,
  SMSalesHome,
  SMCustomersHome,
  SMSectionOrders,
  SMEndOfDay,
  SMReturns,
  SMReconciliationReview,
  SMReconcileStock,
  SMStoreTerminal,
  SMServiceDetails,
} from "../pages/SellModePage";

import {
  AppGuard,
  CashierRestriction,
  RootAppHelper,
  OnboardingGuard,
  BusinessSupplierConsentPrompt,
} from "../components/common";
import { AddIntegratedTerminals } from "../pages/appPages/MarketPlace/IntegratedTerminals/AddIntegratedTerminals";
import ExpenseDepartments from "../pages/appPages/Expenses/ExpenseDepartments";
import { ProductPageCover } from "../components/secondaryComponents/products";
import RestockCover from "../pages/appPages/Inventory/Procurement/Restock/RestockCover";
import { SquareAppGuard } from "../components/secondaryComponents/Chowdeck";
import { StorefrontCover } from "../components/secondaryComponents/storefront";

const useGetRoutes = () => {
  const { platform } = useContext(platformHandler);
  const baseRoute = getPlatform(platform);

  const routes = [
    {
      path: "/",
      element: <Navigate to={pathConstants.LOGIN} replace />,
    },

    // auth
    {
      element: <OnboardingGuard />,
      children: [
        {
          path: pathConstants.SIGNUP,
          element: <authPages.Signup />,
        },
        {
          path: pathConstants.LOGIN,
          element: <authPages.Login />,
        },
        {
          path: pathConstants.LOGIN_VERIFICATION,
          element: <authPages.LoginOtpVerification />,
        },
        {
          path: pathConstants.FORGOT_PASSOWRD,
          element: <authPages.ForgotPassword />,
        },
        {
          path: pathConstants.RESET_PASSWORD,
          element: <authPages.ResetPassword />,
        },
        {
          path: pathConstants.EMAIL_VERIFICATION,
          element: <authPages.EmailVerification />,
        },
        {
          path: pathConstants.SIGNUP_INVITE,
          element: <authPages.InviteSignup />,
        },
        {
          path: pathConstants.SELLMODE_AUTH,
          element: <authPages.SellmodeAuth />,
        },
      ],
    },

    // public pages
    {
      path: pathConstants.PLATFORM_NAVIGATOR,
      element: <secondaryComponents.PlatformNavigator />,
    },
    {
      path: pathConstants.SUBSCRIPTION_PLAN,
      element: <publicPages.SubscriptionView />,
    },
    {
      element: <MenuPageLayout />,
      children: [
        {
          path: pathConstants.MENU({ id: ":id", path: "/*" }),
          element: <publicPages.MenuPage />,
        },
      ],
    },
    {
      path: pathConstants.INVENTORY_STOREFRONT_RIDER_ORDERS({ id: ":id" }),
      element: <publicPages.AllRiderOrders />,
    },
    {
      path: pathConstants.INVENTORY_STOREFRONT_ORDER_DETAILS({ id: ":id" }),
      element: <publicPages.RiderOrderDetails />,
    },
    {
      path: pathConstants.INVENTORY_STOREFRONT_ORDER_STATUS({
        id: ":id",
        menuId: ":menuId",
      }),
      element: <publicPages.OrderStatusTrack />,
    },

    // app routes
    {
      element: (
        <CashierRestriction>
          <BusinessSupplierConsentPrompt>
            <PushNotifications>
              {baseRoute === "/app" ? (
                <AppGuard>
                  <AppLayout />
                </AppGuard>
              ) : (
                <AppGuard>
                  <MobileAppLayout />
                </AppGuard>
              )}
            </PushNotifications>
          </BusinessSupplierConsentPrompt>
        </CashierRestriction>
      ),
      children: [
        {
          path: pathConstants.DASHBOARD_MAIN,
          element: <appPages.MainDashboard />,
        },
        {
          path: pathConstants.DASHBOARD_CONSENT_TO_SUPPLIERS,
          element: <BusinessSupplierConsentHome />,
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector
                permissionParentKey={"Payments"}
                permissionChildKey={
                  "can_view_payment_dashboard_overview_cash_and_customer_analytics"
                }
                redirectPath={pathConstants.PAYMENT_TRANSACTIONS_ALL}
              >
                <DashboardLayout />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              path: pathConstants.DASHBOARD_OVERVIEW,
              element: <appPages.Overview />,
            },
            {
              path: pathConstants.DASHBOARD_CASHFLOW_ANALYTICS,
              element: <appPages.CashflowAnalytics />,
            },
            {
              path: pathConstants.DASHBOARD_CUSTOMER_ANALYTICS,
              element: <appPages.CustomerAnalytics />,
            },
          ],
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector
                permissionParentKey={"Accounting"}
                permissionChildKey={"can_allow_ledger_account"}
              >
                <AccountingLayout beta={true} />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              path: pathConstants.ACCOUNTING_GENERAL_LEDGER,
              element: <appPages.GeneraLedger />,
            },
            {
              path: pathConstants.CHART_OF_ACCOUNTS,
              element: <appPages.ChartOfAccounts />,
            },
            {
              path: pathConstants.REBATES_ACCOUNTINGS,
              element: <appPages.Rebates />,
            },
            {
              path: pathConstants.ACCOUNT_SETTINGS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Accounting"}
                  permissionChildKey="can_view_account_mappings"
                  redirectPath={pathConstants.CHART_OF_ACCOUNTS}
                >
                  <appPages.AccountSettingsPage />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.ACCOUNTING_JOURNAL_ENTRIES,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Accounting"}
                  permissionChildKey="can_view_journal_entries"
                  redirectPath={pathConstants.CHART_OF_ACCOUNTS}
                >
                  <appPages.JournalEntries />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.ACCOUNTING_CREATE_JOURNAL_ENTRY,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Accounting"}
                  permissionChildKey="can_create_journal_entry"
                  redirectPath={pathConstants.CHART_OF_ACCOUNTS}
                >
                  <appPages.CreateJournalEntry />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.ACCOUNTING_EDIT_JOURNAL_ENTRY({
                reference: ":reference",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Accounting"}
                  permissionChildKey="can_create_journal_entry"
                >
                  <appPages.EditJournalEntry />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.ACCOUNTING_JOURNAL_ENTRY_DETAILS({
                reference: ":reference",
              }),
              element: <appPages.JournalEntryDetails />,
            },
            {
              path: pathConstants.ACCOUNTING_CHART_ACCOUNT_TRANSACTION_DETAILS({
                id: ":id",
              }),
              element: <appPages.ChartAccountTransactionDetails />,
            },
            {
              path: pathConstants.ACCOUNTING_REPORTS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Accounting"}
                  permissionChildKey="can_view_accounting_reports"
                >
                  <appPages.AccountingReports />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.ACCOUNTING_CHART_ACCOUNT_DETAILS({
                account_id: ":account_id",
              }),
              element: <appPages.ChartAccountDetails />,
            },
          ],
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector
                permissionParentKey={"Payments"}
                redirectPath={pathConstants.PAYMENT_SUBACCOUNTS}
              >
                <TransfersLayout />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Payments"}
                  permissionChildKey={"can_view_transfer"}
                  redirectPath={pathConstants.PAYMENT_SUBACCOUNTS}
                >
                  <appPages.AllTransfers />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.PAYMENT_TRANSFER_APPROVED,
                  element: <appPages.ApprovedTransfers />,
                },
                {
                  path: pathConstants.PAYMENT_TRANSFER_PENDING,
                  element: <appPages.PendingTransfers />,
                },
                {
                  path: pathConstants.PAYMENT_TRANSFER_REJECTED,
                  element: <appPages.RejectedTransfers />,
                },
                {
                  path: pathConstants.PAYMENT_TRANSFER_SAVEDBENEFICIARY,
                  element: <appPages.SavedBeneficiaries />,
                },
              ],
            },
            {
              path: pathConstants.PAYMENT_TRANSFER_DETAILS({
                transferId: ":transferId",
              }),
              element: <appPages.TransferDetails />,
            },
          ],
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector
                permissionParentKey={"Payments"}
                permissionChildKey={"can_view_transactions_history"}
                redirectPath={pathConstants.PAYMENT_SUBACCOUNTS}
              >
                <TransactionsLayout />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              path: pathConstants.PAYMENT_TRANSACTIONS_ALL,
              element: <appPages.AllTransactions />,
            },
            {
              path: pathConstants.PAYMENT_TRANSACTIONS_DETAILS({
                id: ":transactionId",
              }),
              element: <appPages.TransactionDetails />,
            },
          ],
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector permissionParentKey={"Payments"}>
                <SubAccountLayout />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              path: pathConstants.PAYMENT_SUBACCOUNTS,
              element: <appPages.AllSubAccounts />,
            },
            {
              path: pathConstants.PAYMENT_SUBACCOUNTS_CREATE,
              element: <appPages.CreateSubAccount />,
            },
          ],
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector
                permissionParentKey={"Payments"}
                permissionChildKey="can_view_and_request_pos"
                redirectPath={pathConstants.PAYMENT_SUBACCOUNTS}
              >
                <TerminalsLayout />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              element: <appPages.Terminals />,
              children: [
                {
                  path: pathConstants.PAYMENT_POS_ALL_TERMINALS,
                  element: <appPages.AllTerminals />,
                },
                {
                  path: pathConstants.PAYMENT_POS_REQUEST_TERMINAL,
                  element: <appPages.TerminalRequests />,
                },
              ],
            },
            {
              path: pathConstants.PAYMENT_POS_TERMINAL_DETAILS({
                terminalId: ":terminalId",
              }),
              element: <appPages.TerminalDetails />,
            },
            {
              path: pathConstants.PAYMENT_POS_TERMINAL_CREDENTIALS({
                terminalId: ":terminalId",
              }),
              element: <appPages.TerminalCredentials />,
            },
            {
              path: pathConstants.PAYMENT_POS_ADD_TERMINALS,
              element: <appPages.NewTerminal />,
            },
          ],
        },
        {
          element: (
            <secondaryComponents.CountryChecker>
              <RoutePermissionRedirector permissionParentKey={"Payments"}>
                <TagsLayout />
              </RoutePermissionRedirector>
            </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              path: pathConstants.PAYMENT_ALL_TAGS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Payments"}
                  permissionChildKey="can_view_tags"
                  redirectPath={pathConstants.PAYMENT_SUBACCOUNTS}
                >
                  <appPages.Tags />
                </RoutePermissionRedirector>
              ),
            },
          ],
        },

        // Start of Inventory Module ----------------------------
        {
          element: <ProcurementLayout />,
          children: [
            {
              path: pathConstants.PROCUREMENT_WAYBILLS,
              element: <appPages.Waybills />,
            },
            {
              path: pathConstants.PROCUREMENT_GENERATE_WAYBILL,
              element: <appPages.GenerateWaybill />,
            },
            {
              path: pathConstants.PROCUREMENT_VIEW_WAYBILL({
                id: ":id",
              }),
              element: <appPages.ViewWaybill />,
            },
          ],
        },

        {
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Inventory Management"}
              showOnLocalSetup={true}
            >
              <InventoryLayout isMobileApp={baseRoute === "/mobile"} />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.INVENTORY,
              element: (
                <Navigate to={pathConstants.INVENTORY_ANALYTICS} replace />
              ),
            },
            {
              path: pathConstants.INVENTORY_ANALYTICS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey={"can_view_analytics"}
                  redirectPath={pathConstants.INVENTORY_PRODUCTS}
                  showOnLocalSetup={true}
                >
                  <appPages.InventoryAnalyticsPage />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.ACCOUNTING_VAT_LEDGER,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Accounting"}
                  permissionChildKey={"can_view_vat_logs"}
                  redirectPath={pathConstants.ACCOUNTING_OVERVIEW}
                >
                  <appPages.VatLogsPage />
                </RoutePermissionRedirector>
              ),
            },
            {
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_products"
                  redirectPath={pathConstants.INVENTORY_SALES_TRANSACTIONS}
                  showOnLocalSetup={true}
                >
                  <ProductPageCover />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.INVENTORY_PRODUCTS,
                  element: <appPages.AllProducts />,
                },
                {
                  path: pathConstants.INVENTORY_SERVICES,
                  element: <appPages.AllServices />,
                },
                {
                  path: pathConstants.INVENTORY_COMPOSITE_PRODUCTS,
                  element: <appPages.AllCompositeProducts />,
                },
              ],
            },
            {
              path: pathConstants.INVENTORY_RETURNS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_returns"
                  redirectPath={pathConstants.INVENTORY_SALES_TRANSACTIONS}
                >
                  <appPages.Returns />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_RETURNS_REVIEW({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_returns"
                  redirectPath={pathConstants.INVENTORY_RETURNS}
                >
                  <appPages.ReturnReview />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STOCK_REMOVALS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_stock_removals"
                  redirectPath={pathConstants.INVENTORY_PRODUCTS}
                >
                  <appPages.StockRemovals />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STOCK_REMOVALS_REVIEW({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_stock_removals"
                  redirectPath={pathConstants.INVENTORY_STOCK_REMOVALS}
                >
                  <appPages.StockRemovalReview />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCT_BULK_TRANSFER_HISTORY,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_stock_transfer"
                  localPopUrlPath={
                    pathConstants.INVENTORY_PRODUCT_BULK_TRANSFER_HISTORY
                  }
                >
                  <appPages.StockTransferHistory />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCT_BULK_TRANSFER,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_stock_transfer"
                >
                  <appPages.CreateStockTransfer />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STOCK_TRANSFER_EDIT({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_stock_transfer"
                >
                  <appPages.EditStockTransfer />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_CATEGORIES,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_product_category"
                  showOnLocalSetup={true}
                >
                  <appPages.ProductCategories />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_SALES_TRANSACTIONS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_sales_transaction"
                  showOnLocalSetup={true}
                >
                  <appPages.SalesTransactions />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STOCK_HISTORY,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_stock_history"
                  showOnLocalSetup={true}
                >
                  <appPages.StockHistory />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STOCK_TRANSFER_DETAILS({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_stock_transfer"
                >
                  <appPages.TransferStockDetails />,
                </RoutePermissionRedirector>
              ),
            },
            {
              element: <RestockCover />,
              children: [
                {
                  path: pathConstants.INVENTORY_RESTOCK_LOGS,
                  element: <appPages.ReStockLogs />,
                },
                {
                  path: pathConstants.INVENTORY_RESTOCK_DRAFTS,
                  element: <appPages.RestockDrafts />,
                },
              ],
            },

            {
              path: pathConstants.INVENTORY_SUPPLIERS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_suppliers"
                >
                  <appPages.Suppliers />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_RECONCILIATIONS({}),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_and_submit_reconciliation"
                  localPopUrlPath={pathConstants.INVENTORY_RECONCILIATIONS()}
                >
                  <appPages.AllReconciliations />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCTS_BATCH,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_products"
                >
                  <appPages.ProductsBatch />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCTS_BATCH_EDIT({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_products"
                >
                  <appPages.EditProductsBatch />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_TABLES,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_tables"
                >
                  <appPages.InventoryTables />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_TABLES_DETAILS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_tables"
                >
                  <appPages.InventoryTableDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  showOnLocalSetup={true}
                >
                  <Outlet />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.INVENTORY_STORES,
                  element: (
                    <RoutePermissionRedirector
                      permissionParentKey={"Inventory Management"}
                      permissionChildKey={"can_view_stores"}
                      showOnLocalSetup={true}
                    >
                      <appPages.Stores />
                    </RoutePermissionRedirector>
                  ),
                },
                {},
                {
                  path: pathConstants.INVENTORY_STORES_CREATE,
                  element: (
                    <RoutePermissionRedirector
                      permissionParentKey={"Inventory Management"}
                      permissionChildKey={"can_create_stores"}
                    >
                      <appPages.CreateStore />
                    </RoutePermissionRedirector>
                  ),
                },
                {
                  path: pathConstants.INVENTORY_STORE_EDIT({ id: ":id" }),
                  element: (
                    <RoutePermissionRedirector
                      permissionParentKey={"Inventory Management"}
                      permissionChildKey="can_edit_stores"
                    >
                      <appPages.EditStore />
                    </RoutePermissionRedirector>
                  ),
                },
                {
                  path: pathConstants.INVENTORY_STORE_DETIALS,
                  element: (
                    <RoutePermissionRedirector
                      permissionParentKey={"Inventory Management"}
                      permissionChildKey={"can_edit_stores"}
                    >
                      <appPages.EditStoreDetails />
                    </RoutePermissionRedirector>
                  ),
                },
              ],
            },
            {
              path: pathConstants.INVENTORY_CREDITLOG,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_and_update_credits"
                  showOnLocalSetup={true}
                >
                  <appPages.CreditLog />
                </RoutePermissionRedirector>
              ),
            },
            {
              element: <StorefrontCover />,
              children: [
                {
                  path: pathConstants.INVENTORY_MENU,
                  element: <appPages.AllStorefront />,
                },
                {
                  path: pathConstants.INVENTORY_SHIPPINGS,
                  element: <appPages.AllShippings />,
                },
                {
                  path: pathConstants.INVENTORY_RIDERS,
                  element: <appPages.AllRiders />,
                },
              ],
            },
            {
              path: pathConstants.INVENTORY_STORE_WALLET_LIST,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_store_wallet"
                  showOnLocalSetup={true}
                >
                  <appPages.AllStoreWalllet />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STORE_WALLET_DETAILS({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_store_wallet"
                  showOnLocalSetup={true}
                >
                  <appPages.StoreWalletDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_STORE_WALLET_TRANSACTION_DETAILS({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_store_wallet"
                  showOnLocalSetup={true}
                >
                  <appPages.StoreWalletTransactionDetails />
                </RoutePermissionRedirector>
              ),
            },

            {
              path: pathConstants.INVENTORY_CREDITLOG_DETAILS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_and_update_credits"
                  showOnLocalSetup={true}
                >
                  <appPages.CreditLogDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCT_STOCKHISTORY({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_products"
                >
                  <appPages.ProductStockHistory />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_INVOICE_DETAILS({ id: ":id" }),
              element: <appPages.InvoiceDetails />,
            },
            {
              path: pathConstants.INVENTORY_SUPPLIERS_DETAILS({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_suppliers"
                >
                  <appPages.SupplierDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_SUPPLIERS_DETAILS_EDIT,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_suppliers"
                >
                  <appPages.EditSupplier />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_RECONCILIATIONS_EDIT({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_and_submit_reconciliation"
                >
                  <appPages.ReconcileStocks />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_RECONCILIATIONS_REVIEW({
                id: ":id",
              }),
              element: <appPages.ReconciliationReview />,
            },
            {
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_add_products"
                >
                  <appPages.AddSingleProduct />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.INVENTORY_PRODUCTS_ADD_SINGLE,
                  element: <appPages.CreateProduct />,
                },
                {
                  path: pathConstants.INVENTORY_SERVICE_CREATE,
                  element: <appPages.CreateService />,
                },
              ],
            },

            {
              path: pathConstants.INVENTORY_PRODUCTS_ADD_COMPOSITE,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_add_products"
                >
                  <appPages.CreateCompositeProduct />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_SERVICE_EDIT({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_edit_or_update_product"
                >
                  <appPages.EditService />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCT_EDIT({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_edit_or_update_product"
                >
                  <appPages.EditProduct />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCTS_EDIT_COMPOSITE({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_edit_or_update_product"
                >
                  <appPages.EditCompositeProducts />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCT_ITEM_TRACKING,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  // permissionChildKey="can_edit_or_update_product"
                >
                  <appPages.TrackItem />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCTS_BULK_RESTOCK,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_restock_products_bulk_and_single_restock"
                >
                  <appPages.BulkRestock />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCTS_BULK_RESTOCK_UPDATE({
                restock_id: ":restock_id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_restock_products_bulk_and_single_restock"
                >
                  <appPages.ContinueBulkRestock />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PRODUCTS_ADD_MULTIPLE,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_add_products"
                >
                  <appPages.AddMultipleProducts />
                </RoutePermissionRedirector>
              ),
            },
            {
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_storefront"
                >
                  <appPages.CreateStorefrontCover />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.INVENTORY_MENU_CREATE,
                  element: <appPages.CreateStorfront />,
                },
                {
                  path: pathConstants.INVENTORY_MENU_CREATE_EDIT({
                    menuId: ":menuId",
                  }),
                  element: <appPages.UpdateStorefront />,
                },
                {
                  path: pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION({
                    menuId: ":menuId",
                  }),
                  element: <appPages.CustomizationNavigation />,
                  children: [
                    {
                      path: pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION_CHECKOUT(
                        {
                          menuId: ":menuId",
                        },
                      ),
                      element: <appPages.StorefrontSettingsCheckout />,
                    },
                    {
                      path: pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION_PAYMENT(
                        {
                          menuId: ":menuId",
                        },
                      ),
                      element: <appPages.StorefrontSettingsPayment />,
                    },
                    {
                      path: pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION_TEMPLATES(
                        {
                          menuId: ":menuId",
                        },
                      ),
                      element: <appPages.StorefrontSettingsTemplates />,
                    },
                    {
                      path: pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION_BRANDING(
                        {
                          menuId: ":menuId",
                        },
                      ),
                      element: <appPages.StorefrontSettingsBranding />,
                    },
                    {
                      path: pathConstants.INVENTORY_MENU_DETAILS_CUSTOMIZATION_SORT_CATEGORIES(
                        {
                          menuId: ":menuId",
                        },
                      ),
                      element: <appPages.StorefrontSettingsSortCategories />,
                    },
                  ],
                },
                {
                  path: pathConstants.INVENTORY_MENU_CREATE_SHIPPING({
                    menuId: ":menuId",
                  }),
                  element: <appPages.CreateShippingForm />,
                },
              ],
            },

            {
              path: pathConstants.INVENTORY_MENU_PAYMENT,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_storefront"
                >
                  <appPages.PaymentSetup />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_MENU_PAYMENT_DETAILS({
                paymentId: ":paymentId",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_edit_and_create_storefront"
                >
                  <appPages.PaymentSetup />
                </RoutePermissionRedirector>
              ),
            },

            // Start Purchase Order ===========
            {
              path: pathConstants.INVENTORY_PURCHASE_ORDER,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_purchase_order"
                >
                  <AllPurchaseOrders />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PURCHASE_ORDER_CREATE,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_purchase_order"
                >
                  <CreatePurchaseOrder />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_purchase_order"
                >
                  <PurchaseOrderDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_PURCHASE_ORDER_EDIT({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_purchase_order"
                >
                  <EditPurchaseOrder />
                </RoutePermissionRedirector>
              ),
            },
            // End Purchase Order ===========

            // Start Goods Received ===========
            {
              path: pathConstants.INVENTORY_GOODS_RECEIVED,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_goods_received"
                >
                  <AllGoodsReceived />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_GOODS_RECEIVED_CREATE,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_goods_received"
                >
                  <CreateGoodsReceived />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_GOODS_RECEIVED_DETAILS({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_goods_received"
                >
                  <GoodsReceivedDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_GOODS_RECEIVED_EDIT({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_goods_received"
                >
                  <EditGoodsReceived />
                </RoutePermissionRedirector>
              ),
            },
            // End Goods Received ===========

            // Start Bills ===========
            {
              path: pathConstants.INVENTORY_BILLS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_bill"
                >
                  <AllBills />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_BILLS_CREATE,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_bill"
                >
                  <CreateBills />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_BILLS_DETAILS({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_bill"
                >
                  <BillsDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_BILLS_EDIT({
                id: ":id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_or_update_bill"
                >
                  <EditBills />
                </RoutePermissionRedirector>
              ),
            },
            // End Bills ===========

            // Start PO Categories ===========
            {
              path: pathConstants.INVENTORY_PO_CATEGORIES,
              element: <AllPOCategories />,
            },
            // End Purchase Order ===========
          ],
        },
        // End of Inventory Module ------------------------------

        {
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Inventory Management"}
              showOnLocalSetup={true}
            >
              <StoresLayout />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.STORES,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_view_stores"
                  showOnLocalSetup={true}
                >
                  <appPages.AllStores />
                </RoutePermissionRedirector>
              ),
            },
          ],
        },
        {
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Inventory Management"}
            >
              <AllSectionsLayout />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.SECTIONS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                >
                  <appPages.AllSections />
                </RoutePermissionRedirector>
              ),
            },

            {
              path: pathConstants.MANAGE_SECTIONS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_kitchen_sections"
                >
                  <appPages.ManageSections />
                </RoutePermissionRedirector>
              ),
            },
          ],
        },

        // Start of expense =====================
        {
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Expense"}
              localPopUrlPath={pathConstants.EXPENSES_ANALYTICS}
            >
              <ExpensesLayout />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.EXPENSES_LIST({}) + "/list",
              element: (
                <Navigate to={pathConstants.EXPENSES_LIST({})} replace />
              ),
            },
            {
              path: pathConstants.EXPENSES_ANALYTICS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Expense"}
                  permissionChildKey={"can_view_expense_analytics"}
                  redirectPath={pathConstants.EXPENSES_LIST({})}
                >
                  <appPages.ExpenseAnalytics />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.EXPENSES_LIST({}),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Expense"}
                  permissionChildKey={"can_create_expense"}
                >
                  <appPages.Expenses />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.EXPENSES_BULK_CREATE,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Expense"}
                  // permissionChildKey={
                  //   "can_view_edit_and_create_expense_category"
                  // }
                >
                  <CreateBulkExpense />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.EXPENSES_CATEGORIES,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Expense"}
                  permissionChildKey={
                    "can_view_edit_and_create_expense_category"
                  }
                >
                  <appPages.ExpenseCategories />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.EXPENSES_DEPARTMENTS,
              element: (
                // <RoutePermissionRedirector
                //   permissionParentKey={"Expense"}
                //   permissionChildKey={
                //     "can_view_edit_and_create_expense_category"
                //   }
                // >
                <ExpenseDepartments />
                // </RoutePermissionRedirector>
              ),
            },
          ],
        },
        // End of expense =======================

        // Start of Performance ===================
        {
          element: <PerformanceLayout />,
          children: [
            {
              path: pathConstants.PERFORMANCE_GOALS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey={"can_view_revenue_goal"}
                >
                  <AllGoals />,
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.PERFORMANCE_GOALS_DETAILS({ id: ":id" }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey={"can_view_revenue_goal"}
                >
                  <GoalsDetails />
                </RoutePermissionRedirector>
              ),
            },
          ],
        },
        // End of Performance ====================

        {
          path: pathConstants.EXPENSES_DETAILS({ id: ":id" }),
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Expense"}
              permissionChildKey={"can_create_expense"}
            >
              <appPages.ExpenseDetails />
            </RoutePermissionRedirector>
          ),
        },
        {
          element: (
            <RoutePermissionRedirector permissionParentKey={"Accounting"}>
              <AccountingLayout />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.ACCOUNTING_OVERVIEW,
              element: (
                <RoutePermissionRedirector permissionParentKey={"Accounting"}>
                  <appPages.AccountingOverview />
                </RoutePermissionRedirector>
              ),
            },
          ],
        },
        {
          path: pathConstants.ACCOUNTING_DETAILS({ id: ":id" }),
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Accounting"}
              permissionChildKey={"can_view_all_reports"}
            >
              <appPages.AccountingDetails />
            </RoutePermissionRedirector>
          ),
        },
        {
          path: pathConstants.ACCOUNTING_PROFITANDLOSS,
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Accounting"}
              permissionChildKey={"can_view_all_reports"}
            >
              <appPages.ProfitAndLost />
            </RoutePermissionRedirector>
          ),
        },
        {
          path: pathConstants.REQUEST_CAPITAL,
          element: <appPages.RequestCapital />,
        },
        {
          element: (
            // <secondaryComponents.CountryChecker>
            <RoutePermissionRedirector permissionParentKey={"Loyalty"}>
              <MarketingLayout />
            </RoutePermissionRedirector>
            // </secondaryComponents.CountryChecker>
          ),
          children: [
            {
              path: pathConstants.LOYALTY_MANAGE,
              element: (
                <Navigate to={pathConstants.LOYALTY_MANAGE_CAMPAIGN} replace />
              ),
            },
            {
              path: pathConstants.LOYALTY_CREATE_CAMPAIGN,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Loyalty"}
                  permissionChildKey={"can_view_and_create_loyalty_campaigns"}
                >
                  <appPages.CreateCampaign />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.LOYALTY_CREATE_GIFTCARD,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Loyalty"}
                  permissionChildKey={"can_view_and_create_gift_cards"}
                >
                  <appPages.CreateGiftCard />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.LOYALTY_MANAGE_CAMPAIGN,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Loyalty"}
                  permissionChildKey={"can_view_and_create_loyalty_campaigns"}
                  redirectPath={pathConstants.LOYALTY_MANAGE_GIFTCARDS}
                >
                  <appPages.AllCampaigns />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.LOYALTY_MANAGE_GIFTCARDS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Loyalty"}
                  permissionChildKey={"can_view_and_create_gift_cards"}
                >
                  <appPages.AllGiftCards />
                </RoutePermissionRedirector>
              ),
            },
            {
              element: (
                <RoutePermissionRedirector permissionParentKey={"Loyalty"}>
                  <CustomerSubLayout />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.LOYALTY_CUSTOMERS,
                  element: <appPages.LoyaltyCustomer />,
                },
                {
                  path: pathConstants.LOYALTY_CUSTOMERS_HISTORY,
                  element: <appPages.LoyaltyCustomeHistory />,
                },
              ],
            },
            {
              path: pathConstants.LOYALTY_CUSTOMERS_DETAILS({ id: ":id" }),
              element: (
                <RoutePermissionRedirector permissionParentKey={"Loyalty"}>
                  <appPages.LoyaltyCustomerDetails />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.LOYALTY_NOTIFICATION,
              element: (
                <RoutePermissionRedirector permissionParentKey={"Loyalty"}>
                  <appPages.LoyaltyNotification />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.LOYALTY_EDIT_CAMPAIGN({
                campaignId: ":campaignId",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Loyalty"}
                  permissionChildKey={"can_view_and_create_loyalty_campaigns"}
                >
                  <appPages.EditCampaign />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.LOYALTY_EDIT_GIFTCARD({
                giftcardId: ":giftcardId",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Loyalty"}
                  permissionChildKey={"can_view_and_create_gift_cards"}
                >
                  <appPages.EditGiftCard />
                </RoutePermissionRedirector>
              ),
            },

            // Start SMS Campaign =======================
            {
              element: <SmsCampaignLayout />,
              children: [
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_CUSTOM,
                  element: <appPages.AllSmsCampaigns />,
                },

                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_AUTOMATED,
                  element: <appPages.AllSmsAutomatedCampaigns />,
                },
              ],
            },

            {
              element: <appPages.CreateSmsCampaign />,
              children: [
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_CREATE,
                  element: <appPages.CreateSmsCampaignContext />,
                },
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_CONTEXT({
                    id: ":id",
                  }),
                  element: <appPages.SmsCampaignContext />,
                },
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_AUDIENCE({
                    id: ":id",
                  }),
                  element: <appPages.SmsCampaignAudience />,
                },
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_CONTENT({
                    id: ":id",
                  }),
                  element: <appPages.SmsCampaignContent />,
                },
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_SCHEDULE({
                    id: ":id",
                  }),
                  element: <appPages.SmsCampaignSchedule />,
                },
                {
                  path: pathConstants.MARKETING_SMS_CAMPAIGNS_REVIEW({
                    id: ":id",
                  }),
                  element: <appPages.SmsCampaignReview />,
                },
              ],
            },
            // End SMS Campaign =======================
          ],
        },
        {
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Customers"}
              permissionChildKey={"can_view_customer"}
              showOnLocalSetup={true}
            >
              <CustomersLayout />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.CUSTOMERS,
              element: <appPages.AllCustomers />,
              children: [
                {
                  path: pathConstants.CUSTOMER_CREATE,
                  element: <appPages.CreateCustomer />,
                },
                {
                  path: pathConstants.CUSTOMER_EDIT({
                    customerId: ":customerId",
                  }),
                  element: <appPages.UpdateCustomer />,
                },
              ],
            },
            {
              path: pathConstants.CUSTOMER_DETAILS({
                customerId: ":customerId",
              }),
              element: <appPages.CustomerDetails />,
            },
          ],
        },
        {
          path: pathConstants.APPS_MAIN,
          element: <appPages.AllMarkets />,
        },
        {
          path: pathConstants.APPS_DETAILS({ id: ":id" }),
          element: <appPages.MarketDetails />,
        },

        // Start Chowdeck section =============================
        {
          element: <PerformanceLayout />,
          children: [
            {
              path: pathConstants.APPS_CHOWDECK_CONNECTIONS,
              element: <appPages.AllConnections />,
            },
            {
              path: pathConstants.APPS_CHOWDECK_API_MANAGEMENT,
              element: <appPages.ApiManagement />,
            },
            {
              path: pathConstants.APPS_CHOWDECK_CONNECT_STORE({
                connection_id: ":connection_id",
              }),
              element: <appPages.CreateChowdeckStoreConnection />,
            },
            {
              path: pathConstants.APPS_CHOWDECK_CONNECT_STORE_EDIT({
                connection_id: ":connection_id",
              }),
              element: <appPages.UpdateChowdeckStoreConnection />,
            },
          ],
        },
        {
          path: pathConstants.APPS_CHOWDECK_SETUP,
          element: <appPages.SetupFlow />,
        },
        // End Chowdeck section ================================

        // Start Integrated Terminals ==================

        {
          path: pathConstants.APPS_INTEGRATION_BUSINESS_CONNECTION,
          element: <AllBusinessConnections />,
        },
        {
          path: pathConstants.APPS_INTEGRATION_TERMINALS,
          element: <AllIntegratedTerminals />,
        },
        {
          path: pathConstants.APPS_INTEGRATION_TERMINALS_CONNECT,
          element: <AddIntegratedTerminals />,
        },
        {
          path: pathConstants.APPS_INTEGRATION_TERMINALS_CONNECT_EDIT({
            id: ":id",
          }),
          element: <EditIntegratedTerminals />,
        },
        // End Integrated Terminals ==================

        // Start Squareup ===============================
        {
          element: <PerformanceLayout />,
          children: [
            {
              path: pathConstants.APPS_SQUAREUP,
              element: <appPages.SquareupAuth />,
            },
            {
              path: pathConstants.APPS_SQUAREUP_AUTH_CALLBACK,
              element: <appPages.SquareupAuthCallback />,
            },
            {
              element: <SquareAppGuard />,
              children: [
                {
                  path: pathConstants.APPS_SQUAREUP_CONNECTED_STORE,
                  element: <appPages.SquareupConnections />,
                },
                {
                  path: pathConstants.APPS_SQUAREUP_CONNECT_STORE,
                  element: <appPages.CreateSquareupConnection />,
                },
                {
                  path: pathConstants.APPS_SQUAREUP_CONNECT_STORE_EDIT({
                    connection_id: ":connection_id",
                  }),
                  element: <appPages.UpdateSquareupConnection />,
                },
              ],
            },
          ],
        },
        // End Squareup =================================

        {
          path: pathConstants.KYB_VERIFICATION,
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Settings"}
              permissionChildKey={"can_submit_bvn_and_kyb"}
              redirectPath={pathConstants.DASHBOARD_MAIN}
            >
              <appPages.KYBVerification />
            </RoutePermissionRedirector>
          ),
        },
        {
          path: pathConstants.DOCUMENTATION,
          element: <appPages.Documentation />,
        },
        {
          element: <SettingsLayout />,
          children: [
            {
              path: pathConstants.SETTINGS,
              element: <Navigate to={pathConstants.SETTINGS_PROFILE} replace />,
            },
            {
              path: pathConstants.SETTINGS_USERS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Settings"}
                  permissionChildKey={"can_create_update_and_invite_users"}
                >
                  <appPages.Users />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.SETTINGS_SECURITY_PIN_QUESTION,
              element: <appPages.SecurityPinAndQuestion />,
            },
            {
              path: pathConstants.SETTINGS_SETTLEMENTS,
              element: (
                <secondaryComponents.CountryChecker>
                  <RoutePermissionRedirector
                    permissionParentKey={"Settings"}
                    permissionChildKey={
                      "can_edit_and_update_settlement_accounts"
                    }
                  >
                    <appPages.Settlements />
                  </RoutePermissionRedirector>
                </secondaryComponents.CountryChecker>
              ),
            },
            {
              path: pathConstants.SETTINGS_APIKEYS,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Settings"}
                  permissionChildKey={"can_view_update_api_key"}
                >
                  <appPages.ApiKeys />
                </RoutePermissionRedirector>
              ),
            },
            {
              element: <appPages.Profile />,
              children: [
                {
                  path: pathConstants.SETTINGS_PROFILE,
                  element: <appPages.UserProfile />,
                },
                {
                  path: pathConstants.SETTINGS_BUSINESS_PROFILE,
                  element: (
                    <RoutePermissionRedirector
                      permissionParentKey={"Settings"}
                      permissionChildKey="can_update_business_profile"
                      redirectPath={pathConstants.SETTINGS_PROFILE}
                    >
                      <appPages.BusinessProfile />
                    </RoutePermissionRedirector>
                  ),
                },
              ],
            },
            {
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Settings"}
                  permissionChildKey={"can_update_subscriptions"}
                >
                  <appPages.Subscriptions />
                </RoutePermissionRedirector>
              ),
              children: [
                {
                  path: pathConstants.SETTINGS_SUBSCRIPTIONS,
                  element: <appPages.SubscriptionHistory />,
                },
                {
                  path: pathConstants.SETTINGS_SUBSCRIPTIONS_PLANS,
                  element: <appPages.SubscriptionPlans />,
                },
              ],
            },
            {
              path: pathConstants.SETTINGS_ACTIVITY_LOGS,
              element: <AllActivityLogs />,
            },
            {
              path: pathConstants.SETTINGS_ACTIVITY_LOGS_DETAILS({ id: ":id" }),
              element: <ActivityLogDetails />,
            },
            {
              path: pathConstants.AI_ASSISTANCE,
              element: <appPages.AIAssistance />,
            },
            {
              path: pathConstants.LUMI_AI_ASSISTANT,
              element: <appPages.LumiAiAssistant />,
            },
            {
              path: pathConstants.LUMI_AI_ASSISTANT_CONVERSATION,
              element: <appPages.LumiAiAssistant />,
            },
            {
              path: pathConstants.SETTINGS_PLATFORM_CREDIT,
              element: <appPages.PlatformCreditHistory />,
              children: [
                {
                  path: pathConstants.SETTINGS_PLATFORM_CREDIT_TOPUP,
                  element: <appPages.PlatformCreditTopUp />,
                },
              ],
            },
          ],
        },
        {
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Settings"}
              permissionChildKey={"can_create_update_and_invite_users"}
            >
              <appPages.ManageRoles />
            </RoutePermissionRedirector>
          ),
          children: [
            {
              path: pathConstants.SETTINGS_USERS_MANAGEROLES,
              element: <appPages.DefaultRoles />,
            },
            {
              path: pathConstants.SETTINGS_USERS_MANAGEROLES_DEFAULT,
              element: <appPages.DefaultRoles />,
            },
            {
              path: pathConstants.SETTINGS_USERS_MANAGEROLES_CUSTOM,
              element: <appPages.CustomRoles />,
            },
          ],
        },
        {
          path: pathConstants.SETTINGS_USERS_MANAGEROLES_DETAILS({ id: ":id" }),
          element: (
            <RoutePermissionRedirector
              permissionParentKey={"Settings"}
              permissionChildKey={"can_create_update_and_invite_users"}
            >
              <appPages.RolePage />
            </RoutePermissionRedirector>
          ),
        },
        {
          path: pathConstants.APP_SUBSCRIPTION_PLAN,
          element: <appPages.SubscriptionSelection />,
        },
        {
          path: pathConstants.INVENTORY_PRINTER,
          element: <appPages.AllSettings />,
          children: [
            {
              path: pathConstants.INVENTORY_PRINTER_CONFIGURATION,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  showOnLocalSetup={true}
                >
                  <appPages.PrinterConfiguration />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.APP_RECEIPT_CUSTOMIZATION,
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  showOnLocalSetup={true}
                >
                  <appPages.PrintCustomaizationPage />
                </RoutePermissionRedirector>
              ),
            },
          ],
        },
      ],
    },

    {
      element: <RootAppHelper />,
      children: [
        {
          path: pathConstants.CASHIER_STORES,
          element: <appPages.CashierStores />,
        },
        {
          element: (
            <PushNotifications>
              <SellModeLayoutV2 />
            </PushNotifications>
          ),
          children: [
            {
              path: pathConstants.SELL_MODE({ store_id: ":store_id" }),
              element: <PointOfSaleProducts />,
            },
            {
              path: pathConstants.SELL_MODE_CHECKOUT({
                store_id: ":store_id",
              }),
              element: <CheckoutHome />,
            },
            {
              path: pathConstants.SELL_MODE_BILLS({
                store_id: ":store_id",
              }),
              element: <BillsHome />,
            },
            {
              path: pathConstants.SELL_MODE_SALES({
                store_id: ":store_id",
              }),
              element: <SMSalesHome />,
            },
            {
              path: pathConstants.SELL_MODE_CUSTOMERS({
                store_id: ":store_id",
              }),
              element: <SMCustomersHome />,
            },
            {
              path: pathConstants.SELL_MODE_SECTION_ORDERS({
                store_id: ":store_id",
              }),
              element: <SMSectionOrders />,
            },
            {
              path: pathConstants.SELL_MODE_ENDOFDAY({
                store_id: ":store_id",
              }),
              element: <SMEndOfDay />,
            },
            {
              path: pathConstants.SELL_MODE_RETURNS({
                store_id: ":store_id",
              }),
              element: <SMReturns />,
            },
            {
              path: pathConstants.SELL_MODE_PENDING_CHECKOUT({
                store_id: ":store_id",
              }),
              element: <PendingCheckoutHome />,
            },
            {
              path: pathConstants.SELL_MODE_ORDER_EXTRA({
                store_id: ":store_id",
                order_id: ":order_id",
              }),
              element: <OrderExtra />,
            },
            {
              path: pathConstants.SELL_MODE_BILL_CHECKOUT({
                store_id: ":store_id",
                order_id: ":order_id",
              }),
              element: <BillCheckoutContinuation />,
            },
            {
              path: pathConstants.SELL_MODE_TERMINAL_CONFIG({
                store_id: ":store_id",
              }),
              element: <SMStoreTerminal />,
            },
            {
              path: pathConstants.INVENTORY_RECONCILIATIONS({
                store_id: ":store_id",
                isSellMode: true,
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_and_submit_reconciliation"
                  showOnLocalSetup={true}
                >
                  <SMReconciliationHome />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_RECONCILIATIONS_EDIT({
                store_id: ":store_id",
                id: ":id",
                isSellMode: true,
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Inventory Management"}
                  permissionChildKey="can_create_and_submit_reconciliation"
                  showOnLocalSetup={true}
                >
                  <SMReconcileStock />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.INVENTORY_RECONCILIATIONS_REVIEW({
                store_id: ":store_id",
                id: ":id",
                isSellMode: true,
              }),
              element: <SMReconciliationReview />,
            },
            {
              path: pathConstants.EXPENSES_LIST({
                isSellMode: true,
                store_id: ":store_id",
              }),
              element: (
                <RoutePermissionRedirector
                  permissionParentKey={"Expense"}
                  permissionChildKey={"can_create_expense"}
                  showOnLocalSetup={true}
                >
                  <SMExpenseHome />
                </RoutePermissionRedirector>
              ),
            },
            {
              path: pathConstants.SELL_MODE_SERVICE_DETAILS({
                isSellMode: true,
                store_id: ":store_id",
                service_id: ":service_id",
              }),
              element: <SMServiceDetails />,
            },
          ],
        },
        {
          element: <SectionLayout />,
          children: [
            {
              path: pathConstants.SECTION_MODE({ section_id: ":section_id" }),
              element: <SectionPages.StaffSection />,
            },
          ],
        },
      ],
    },

    {
      element: <RootAppHelper />,
      children: [
        {
          element: <SectionPages.CustomersView />,
          path: pathConstants.SECTION_CUSTOMER_MODE({ store_id: ":store_id" }),
        },
      ],
    },

    // helper page
    {
      element: <ClientLogin />,
      path: pathConstants.CLIENT_LOGIN,
    },
    {
      element: (
        <AppGuard checkSubscription={false}>
          <ExistingClientConnect />
        </AppGuard>
      ),
      path: pathConstants.CLIENT_EXISTING_CONNECT,
    },
    {
      element: <MobileComTest />, // This is a helper page for mobile communication testing
      path: pathConstants.TEST_MOBILE_COM,
    },

    { path: "*", element: <Error404 /> },
  ];

  return routes;
};

export default useGetRoutes;
